// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { Bip39Words } from './bip39-wordlist.js';
import { bin, bytes, dec, utf8 } from './convert.js';
import { BIP39_ITERATIONS } from './constants.js';
import { Entropy } from './entropy.js';
const { subtle } = globalThis.crypto;
/**
* Represents a mnemonic phrase that identifies a wallet as defined by BIP-39.
*/
export class Bip39Mnemonic {
    static #isInternal = false;
    #bip44Seed = '';
    #blake2bSeed = '';
    #phrase = '';
    get phrase() { return this.#phrase.normalize('NFKD'); }
    constructor() {
        if (!Bip39Mnemonic.#isInternal) {
            throw new Error(`Bip39Mnemonic cannot be instantiated directly. Use 'await Bip39Mnemonic.fromPhrase()' or 'await Bip39Mnemonic.fromEntropy() instead.`);
        }
        Bip39Mnemonic.#isInternal = false;
    }
    /**
    * Imports and validates an existing mnemonic phrase.
    *
    * The phrase must be valid according to the BIP-39 specification. Typically
    * wallets use the maximum of 24 words.
    *
    * @param {string} phrase - String of 12, 15, 18, 21, or 24 words
    * @returns {string} Mnemonic phrase validated using the BIP-39 wordlist
    */
    static async fromPhrase(phrase) {
        Bip39Mnemonic.#isInternal = true;
        const self = new this();
        const isValid = await Bip39Mnemonic.validate(phrase);
        if (isValid) {
            self.#phrase = phrase.normalize('NFKD');
            return self;
        }
        else {
            throw new Error('Invalid mnemonic phrase.');
        }
    }
    /**
    * Derives a mnemonic phrase from source of entropy or seed.
    *
    * The entropy must be between 32-64 characters to stay within the defined
    * limit of 128-256 bits. Typically wallets use the maximum entropy allowed.
    *
    * @param {string} entropy - Hexadecimal string
    * @returns {string} Mnemonic phrase created using the BIP-39 wordlist
    */
    static async fromEntropy(entropy) {
        const e = new Entropy(entropy);
        const checksum = await this.checksum(e);
        let concatenation = `${e.bits}${checksum}`;
        const words = [];
        while (concatenation.length > 0) {
            const wordBits = concatenation.substring(0, 11);
            const wordIndex = parseInt(wordBits, 2);
            words.push(Bip39Words[wordIndex]);
            concatenation = concatenation.substring(11);
        }
        const sentence = words.join(' ');
        return Bip39Mnemonic.fromPhrase(sentence);
    }
    /**
     * SHA-256 hash of entropy that is appended to the entropy and subsequently
     * used to generate the mnemonic phrase.
     *
     * @param {Entropy} entropy - Cryptographically strong pseudorandom data of length N bits
     * @returns First N/32 bits of the hash as a hexadecimal string
     */
    static async checksum(entropy) {
        const hashBuffer = await subtle.digest('SHA-256', entropy.bytes);
        const hashBytes = new Uint8Array(hashBuffer);
        const hashBits = bytes.toBin(hashBytes);
        const checksumLength = entropy.bits.length / 32;
        const checksum = hashBits.substring(0, checksumLength);
        return checksum;
    }
    /**
    * Validates a mnemonic phrase.
    *
    * @param {string} mnemonic - Mnemonic phrase to validate
    * @returns {boolean} True if the mnemonic phrase is valid
    */
    static async validate(mnemonic) {
        const words = mnemonic.normalize('NFKD').split(' ');
        if (words.length % 3 !== 0) {
            return false;
        }
        const wordBits = words.map(word => {
            const wordIndex = Bip39Words.indexOf(word);
            if (wordIndex === -1) {
                return false;
            }
            return dec.toBin(wordIndex, 11);
        }).join('');
        const checksumLength = wordBits.length / 33;
        const entropyLength = wordBits.length - checksumLength;
        const entropyBits = wordBits.substring(0, entropyLength);
        const checksumBits = wordBits.substring(entropyLength);
        if (entropyBits == null
            || entropyBits.length < 128
            || entropyBits.length > 256
            || entropyBits.length % 32 !== 0) {
            return false;
        }
        const entropy = new Entropy(bin.toBytes(entropyBits));
        const expectedChecksum = await this.checksum(entropy);
        if (expectedChecksum !== checksumBits) {
            return false;
        }
        return true;
    }
    /**
    * Converts the mnemonic phrase to a BIP-39 seed.
    *
    * A passphrase string can be specified. If the passphrase is undefined, null,
    * or not a string, the empty string ("") is used instead.
    *
    * @param {string} [passphrase=''] - Used as the PBKDF2 salt. Default: ""
    * @returns {string} Hexadecimal seed
    */
    async toBip39Seed(passphrase) {
        if (this.#blake2bSeed === '') {
            if (passphrase == null || typeof passphrase !== 'string') {
                passphrase = '';
            }
            passphrase = `mnemonic${passphrase.normalize('NFKD')}`;
            const derivationAlgorithm = {
                name: 'PBKDF2',
                hash: 'SHA-512',
                salt: utf8.toBytes(passphrase),
                iterations: BIP39_ITERATIONS
            };
            const phraseKey = await subtle.importKey('raw', utf8.toBytes(this.phrase), 'PBKDF2', false, ['deriveBits', 'deriveKey']);
            const derivedKeyType = {
                name: 'HMAC',
                hash: 'SHA-512',
                length: 512
            };
            const isSeedKeyExtractable = true;
            const seedKeyUsages = ['sign'];
            const seedKey = await subtle.deriveKey(derivationAlgorithm, phraseKey, derivedKeyType, isSeedKeyExtractable, seedKeyUsages);
            const seedBuffer = await subtle.exportKey('raw', seedKey);
            const seedBytes = new Uint8Array(seedBuffer);
            const seed = bytes.toHex(seedBytes);
            this.#bip44Seed = seed;
        }
        return this.#bip44Seed;
    }
    /**
    * Converts the mnemonic phrase to a BLAKE2b seed.
    *
    * @returns {string} Hexadecimal seed
    */
    async toBlake2bSeed() {
        if (this.#blake2bSeed === '') {
            const wordArray = this.phrase.split(' ');
            const bits = wordArray.map((w) => {
                const wordIndex = Bip39Words.indexOf(w);
                if (wordIndex === -1) {
                    return false;
                }
                return dec.toBin(wordIndex, 11);
            }).join('');
            const dividerIndex = Math.floor(bits.length / 33) * 32;
            const entropyBits = bits.slice(0, dividerIndex);
            const entropyBytes = entropyBits.match(/(.{1,8})/g)?.map((bin) => parseInt(bin, 2));
            if (entropyBytes == null) {
                throw new Error('Invalid mnemonic phrase');
            }
            this.#blake2bSeed = bytes.toHex(Uint8Array.from(entropyBytes));
        }
        return this.#blake2bSeed;
    }
}
